import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

// Lazy loading des pages pour améliorer les performances
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SMCPage = lazy(() => import("./pages/SMCPage"));
const BacktestHistory = lazy(() => import("./pages/BacktestHistory"));
const PoZousBot = lazy(() => import("./pages/OptimizationBot"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const FearGreedPage = lazy(() => import("./pages/FearGreedPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const TradeHistoryPage = lazy(() => import("./pages/TradeHistoryPage"));
const BotHistoryPage = lazy(() => import("./pages/BotHistoryPage"));
const WinningTradesPage = lazy(() => import("./pages/WinningTradesPage"));
const LosingTradesPage = lazy(() => import("./pages/LosingTradesPage"));
const PerformancePage = lazy(() => import("./pages/PerformancePage"));
const PozeusePage = lazy(() => import("./pages/PozeusePage"));
const TradesHubPage = lazy(() => import("./pages/TradesHubPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const IndicatorsGuidePage = lazy(() => import("./pages/IndicatorsGuidePage"));
const BotSettingsPage = lazy(() => import("./pages/BotSettingsPage"));
const PropFirmPage = lazy(() => import("./pages/PropFirmPage"));
const PropFirmSimulatorPage = lazy(() => import("./pages/PropFirmSimulatorPage"));
const UserSimulatorPage = lazy(() => import("./pages/UserSimulatorPage"));
// const BitgetPage = lazy(() => import("./pages/BitgetPage")); // DESACTIVE 30/04/2026
const MetaTraderPage = lazy(() => import("./pages/MetaTraderPage"));
const FullHistoryPage = lazy(() => import("./pages/FullHistoryPage"));
const MT5LivePage = lazy(() => import("./pages/MT5LivePage"));
const FundedAccountPage = lazy(() => import("./pages/FundedAccountPage"));
const RRRTrackerPage = lazy(() => import("./pages/RRRTrackerPage"));
const BlacklistPage = lazy(() => import("./pages/BlacklistPage"));
const DebriefPage = lazy(() => import("./pages/DebriefPage"));
const AIAssistantPage = lazy(() => import("./pages/AIAssistantPage"));
const AIDoctorPage = lazy(() => import("./pages/AIDoctorPage"));

// Composant de chargement global - Ultra rapide
const PageLoader = () => (
  <div className="min-h-screen bg-[#050505]" />
);

// Protected Route Component - Vérifie auth OU code d'accès demo
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const hasLegacyAccess = localStorage.getItem("tc_access") === "granted";

  if (loading) {
    return <PageLoader />;
  }

  // Accès si authentifié OU si code demo (tc_access)
  if (!isAuthenticated && !hasLegacyAccess) {
    return <Navigate to="/auth" replace />;
  }

  return children;
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const [hasLegacyAccess, setHasLegacyAccess] = useState(false);

  useEffect(() => {
    const access = localStorage.getItem("tc_access");
    if (access === "granted") {
      setHasLegacyAccess(true);
    }
  }, []);

  const handleAccessGranted = () => {
    setHasLegacyAccess(true);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050505]" />;
  }

  const hasAccess = isAuthenticated || hasLegacyAccess;

  return (
    <div className="App min-h-screen bg-[#050505]">
      <div className="noise-overlay" />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Landing Page */}
            <Route 
              path="/" 
              element={
                hasAccess ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <LandingPage onAccessGranted={handleAccessGranted} />
                )
              } 
            />

          {/* Auth Page */}
          <Route 
            path="/auth" 
            element={
              hasAccess ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AuthPage />
              )
            } 
          />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/wallet" element={<Navigate to="/dashboard" replace />} />
          <Route path="/indicators-guide" element={<ProtectedRoute><IndicatorsGuidePage /></ProtectedRoute>} />
          <Route path="/smc" element={<ProtectedRoute><SMCPage /></ProtectedRoute>} />
          <Route path="/backtest" element={<ProtectedRoute><BacktestHistory /></ProtectedRoute>} />
          <Route path="/bot" element={<ProtectedRoute><PoZousBot /></ProtectedRoute>} />
          <Route path="/fear-greed" element={<ProtectedRoute><FearGreedPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
          <Route path="/trades-history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
          <Route path="/bot-history" element={<ProtectedRoute><BotHistoryPage /></ProtectedRoute>} />
          <Route path="/winning-trades" element={<ProtectedRoute><WinningTradesPage /></ProtectedRoute>} />
          <Route path="/losing-trades" element={<ProtectedRoute><LosingTradesPage /></ProtectedRoute>} />
          <Route path="/performance" element={<ProtectedRoute><PerformancePage /></ProtectedRoute>} />
          <Route path="/potential-trades" element={<Navigate to="/dashboard" replace />} />
          <Route path="/trades-hub" element={<ProtectedRoute><TradesHubPage /></ProtectedRoute>} />
          <Route path="/pozeuse" element={<ProtectedRoute><PozeusePage /></ProtectedRoute>} />
          <Route path="/trading-bot" element={<Navigate to="/challenge" replace />} />
          <Route path="/bot-settings" element={<ProtectedRoute><BotSettingsPage /></ProtectedRoute>} />
          <Route path="/prop-firm" element={<ProtectedRoute><PropFirmPage /></ProtectedRoute>} />
          <Route path="/challenge" element={<ProtectedRoute><UserSimulatorPage /></ProtectedRoute>} />
          <Route path="/simulator" element={<Navigate to="/challenge" replace />} />
          <Route path="/simulator-admin" element={<ProtectedRoute><PropFirmSimulatorPage /></ProtectedRoute>} />
          <Route path="/bitget" element={<Navigate to="/dashboard" replace />} />
          <Route path="/metatrader" element={<ProtectedRoute><MetaTraderPage /></ProtectedRoute>} />
          <Route path="/full-history" element={<ProtectedRoute><FullHistoryPage /></ProtectedRoute>} />
          <Route path="/mt5-live" element={<ProtectedRoute><MT5LivePage /></ProtectedRoute>} />
          <Route path="/funded" element={<ProtectedRoute><FundedAccountPage /></ProtectedRoute>} />
          <Route path="/rrr-tracker" element={<ProtectedRoute><RRRTrackerPage /></ProtectedRoute>} />
          <Route path="/blacklist" element={<ProtectedRoute><BlacklistPage /></ProtectedRoute>} />
          <Route path="/debrief" element={<ProtectedRoute><DebriefPage /></ProtectedRoute>} />
          <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistantPage /></ProtectedRoute>} />
          <Route path="/ai-doctor" element={<ProtectedRoute><AIDoctorPage /></ProtectedRoute>} />
          <Route path="/tradelocker" element={<Navigate to="/metatrader" replace />} />
          
          {/* Admin Route */}
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
